import classNames from "classnames";
import React from "react";
import "./origin-grid.scss";

export interface OriginGridValue {
  x: number;
  y: number;
}

export interface OriginGridProps {
  /** Current value in percentages (0–100). Defaults to {x: 50, y: 50} when undefined. */
  value?: OriginGridValue;
  /** Called when a dot is clicked. Always emits one of the nine corner/edge/center positions. */
  onChange?: (value: OriginGridValue) => void;
  /** Disable interaction (greys out dots). */
  disabled?: boolean;
  /** Test id for the wrapper. */
  dataTestId?: string;
  className?: string;
}

const GRID_AXIS: readonly number[] = [0, 50, 100] as const;

/**
 * OriginGrid — 3×3 dot widget used inside the Transform Settings popover
 * (and the Children perspective sub-card). The nine dots represent the
 * combinations of {0, 50, 100}% on both axes. Clicking snaps the value
 * to that combination. The Left/Top % inputs that sit beside the grid
 * are owned by the parent component and synchronised via value/onChange —
 * the grid does not own that state, which prevents the two surfaces from
 * fighting each other.
 *
 * Active dot detection: a dot is "active" only when both axes are exactly
 * one of {0, 50, 100}; arbitrary numeric input from the parent (e.g. 25%)
 * leaves the grid with no active dot but does not block clicks.
 */
const OriginGrid: React.FC<OriginGridProps> = ({ value, onChange, disabled, dataTestId, className }) => {
  const currentX: number = value?.x ?? 50;
  const currentY: number = value?.y ?? 50;

  const handleDotClick = (nextX: number, nextY: number): void => {
    if (disabled) return;
    onChange?.({ x: nextX, y: nextY });
  };

  return (
    <div
      role="group"
      aria-label="Origin grid"
      data-testid={dataTestId}
      className={classNames("origin-grid", className, {
        "origin-grid--disabled": disabled
      })}
    >
      {GRID_AXIS.map((yPercent: number) => (
        <div key={`row-${yPercent}`} className="origin-grid__row">
          {GRID_AXIS.map((xPercent: number) => {
            const isActive: boolean = currentX === xPercent && currentY === yPercent;
            return (
              <button
                key={`dot-${xPercent}-${yPercent}`}
                type="button"
                aria-label={`Origin ${xPercent}% ${yPercent}%`}
                aria-pressed={isActive}
                disabled={disabled}
                className={classNames("origin-grid__dot", {
                  "origin-grid__dot--active": isActive
                })}
                onClick={(): void => handleDotClick(xPercent, yPercent)}
                data-testid={dataTestId ? `${dataTestId}-dot-${xPercent}-${yPercent}` : undefined}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default OriginGrid;
